"use client"

import { useMemo } from "react"
import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"

export class ApiError extends Error {
  /**
   * Server-friendly error string lifted out of the response body when present.
   * FBO controllers conventionally return `{ error: "human-readable reason" }`,
   * while the original Phoenix middleware uses `{ message: "..." }`. Both shapes
   * resolve to the same string here so callers can `toast(error.detail)` without
   * having to know which backend produced it. Falls back to the HTTP status when
   * neither field is present.
   *
   * Surface this on toast / inline error UI instead of `error.message`, which
   * still carries the "API request failed: METHOD URL - ..." prefix for log lines.
   */
  public readonly detail: string

  constructor(
    public readonly method: string,
    public readonly url: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    // Pull the human-readable reason out of the response body. The two conventions
    // we see in production:
    //   FBO controllers           → { error: "...", ... }
    //   Phoenix middleware        → { message: "...", ... }
    //   Some 409 conflict paths   → { error: "CONFLICT:..." } (prefix stripped on
    //                                the service side before it reaches the wire)
    // Anything else collapses to the bare status code so we still produce *some*
    // text rather than "[object Object]".
    let extracted: string | null = null
    if (body != null && typeof body === "object") {
      const b = body as Record<string, unknown>
      if (typeof b.error === "string" && b.error.length > 0) extracted = b.error
      else if (typeof b.message === "string" && b.message.length > 0) extracted = b.message
    }
    const detail = extracted ?? String(status)
    super(`API request failed: ${method} ${url} - ${detail}`)
    this.name = "ApiError"
    this.detail = detail
  }
}

export class NetworkError extends Error {
  constructor(
    public readonly url: string,
    public readonly method: string | undefined,
    cause: unknown,
  ) {
    super(`Network request failed: ${method} ${url}`, { cause })
    this.name = "NetworkError"
  }
}

// API endpoints
export const API_ENDPOINTS = {
  // Main API
  MAIN: {
    BASE_URL: env.apiUrl,
    // Add specific endpoints here
    USERS: "/users",
    // ...
  },
  // Admin Service API
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
    // Add specific endpoints here
    SETTINGS: "/settings",
    // ...
  },
  // Workflow API
  WORKFLOW: {
    BASE_URL: env.workflowApiUrl,
    // Add specific endpoints here
    // ...
  },
  // FBO Report API
  FBO_REPORT: {
    BASE_URL: env.fboReportApiUrl,
    // Add specific endpoints here
    REPORT_PEOPLE: "/report/33",
    // ...
  },
  // Sync API (OB Portal / gorilla-sync)
  SYNC: {
    BASE_URL: env.syncApiUrl,
  },
  // ATS Service API
  ATS_SERVICE: {
    BASE_URL: env.atsServiceUrl,
    PLACEMENT_REQUESTS: "/api/placement-requests",
    SYNC_SETTINGS: "/api/sync-settings",
    SYNC_SETTINGS_CAN_MANAGE: "/api/sync-settings/can-manage",
  },
  // Onboarding Service API
  ONBOARDINGFORM: {
    BASE_URL: env.onboardingFormApiUrl,
  },
}

// Custom hook for making authenticated API requests
export function useApi() {
  const auth = useAuth()

  // Generic fetch function with authentication and token retry logic
  const fetchWithAuth = useMemo(() =>
    async <T,>(url: string, options: RequestInit & { skipRetryOn401?: boolean } = {}): Promise<T> => {
    const { skipRetryOn401, ...fetchOptions } = options
    const currentToken = auth.token || tokenManager.getLastToken()
    const headers = new Headers(options.headers)

    // Apply base path if the URL is relative and doesn't start with http or //
    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    // Add authorization header if token exists
    if (currentToken) {
      headers.set("Authorization", `Bearer ${currentToken}`)
    }

    try {
      const response = await fetch(finalUrl, {
        ...fetchOptions,
        headers,
      })

      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          console.warn("Received 401 Unauthorized from", finalUrl)

          // If caller opted out of retry, throw immediately
          if (skipRetryOn401) {
            throw new ApiError(options.method!, finalUrl, 401, null)
          }

          // Only request a new token if we haven't already requested one
          if (tokenManager.shouldRequestToken()) {
            console.log("Requesting new token from parent")
            requestTokenDueToAuthFailure(`401 Unauthorized from ${finalUrl}`)
          }

          // Queue this request to be retried when we get a new token
          console.log("Queueing request for retry when new token is received")
          const retriedResponse = await tokenManager.queueRequest(finalUrl, {
            ...fetchOptions,
            headers,
          })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => null)
            throw new ApiError(options.method!, finalUrl, retriedResponse.status, errorData)
          }

          // For non-JSON responses, return the response object
          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }

          return retriedResponse as unknown as T
        }

        // Handle other error responses
        const errorData = await response.json().catch(() => null)
        throw new ApiError(options.method!, finalUrl, response.status, errorData)
      }

      // For non-JSON responses, return the response object
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return response.json()
      }

      return response as unknown as T
    } catch (error) {
      if (error instanceof ApiError) throw error
      if (error instanceof TypeError) throw new NetworkError(finalUrl, options.method, error)
      throw error
    }
  }, [auth.token])

  // Helper methods for common HTTP methods
  const get = useMemo(() => 
    <T,>(endpoint: string, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, { ...options, method: "GET" })
  , [fetchWithAuth])

  const post = useMemo(() =>
    <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, {
        ...options,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        body: JSON.stringify(data),
      })
  , [fetchWithAuth])

  const put = useMemo(() =>
    <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, {
        ...options,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        body: JSON.stringify(data),
      })
  , [fetchWithAuth])

  const patch = useMemo(() =>
    <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, {
        ...options,
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        body: JSON.stringify(data),
      })
  , [fetchWithAuth])

  const del = useMemo(() =>
    <T,>(endpoint: string, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })
  , [fetchWithAuth])

  // Create full URLs for each API
  const createUrl = (baseUrl: string, path: string) => {
    // Remove trailing slash from base URL if present
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    // Remove leading slash from path if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }

  // API-specific methods
  const mainApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  const adminApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  const workflowApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  const fboReportApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  const syncApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.SYNC.BASE_URL, path), options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.SYNC.BASE_URL, path), data, options),
  }), [auth.token, get, put])

  const onboardingFormApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ONBOARDINGFORM.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ONBOARDINGFORM.BASE_URL, path), data, options),
  }), [auth.token, get, post])

  const atsServiceApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ATS_SERVICE.BASE_URL, path), options),
    post: <T,>(path: string, data?: unknown, options?: RequestInit) => post<T>(createUrl(API_ENDPOINTS.ATS_SERVICE.BASE_URL, path), data, options),
    put: <T,>(path: string, data?: unknown, options?: RequestInit) => put<T>(createUrl(API_ENDPOINTS.ATS_SERVICE.BASE_URL, path), data, options),
    patch: <T,>(path: string, data?: unknown, options?: RequestInit) => patch<T>(createUrl(API_ENDPOINTS.ATS_SERVICE.BASE_URL, path), data, options),
  }), [auth.token, get, post, put, patch])

  return {
    mainApi,
    adminApi,
    workflowApi,
    fboReportApi,
    syncApi,
    onboardingFormApi,
    atsServiceApi,
    fetchWithAuth,
  }
} 